import { elementSymbol } from './elements.ts';

export interface PeriodicTableCell {
  readonly z: number;
  readonly symbol: string;
  readonly name: string;
  // 1-7 for the main block; 8 = the lanthanide strip, 9 = the actinide strip.
  readonly period: number;
  // 1-18, matching the standard wide form.
  readonly column: number;
}

const ELEMENT_NAMES = `Hydrogen Helium Lithium Beryllium Boron Carbon Nitrogen Oxygen Fluorine Neon
Sodium Magnesium Aluminium Silicon Phosphorus Sulfur Chlorine Argon Potassium Calcium
Scandium Titanium Vanadium Chromium Manganese Iron Cobalt Nickel Copper Zinc
Gallium Germanium Arsenic Selenium Bromine Krypton Rubidium Strontium Yttrium Zirconium
Niobium Molybdenum Technetium Ruthenium Rhodium Palladium Silver Cadmium Indium Tin
Antimony Tellurium Iodine Xenon Caesium Barium Lanthanum Cerium Praseodymium Neodymium
Promethium Samarium Europium Gadolinium Terbium Dysprosium Holmium Erbium Thulium Ytterbium
Lutetium Hafnium Tantalum Tungsten Rhenium Osmium Iridium Platinum Gold Mercury
Thallium Lead Bismuth Polonium Astatine Radon Francium Radium Actinium Thorium
Protactinium Uranium Neptunium Plutonium Americium Curium Berkelium Californium Einsteinium Fermium
Mendelevium Nobelium Lawrencium Rutherfordium Dubnium Seaborgium Bohrium Hassium Meitnerium Darmstadtium
Roentgenium Copernicium Nihonium Flerovium Moscovium Livermorium Tennessine Oganesson`.split(/\s+/);

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

// Each segment lays consecutive atomic numbers (`from`, `from + 1`, ...) into the given columns
// of one period row. Period 6/7 split around the f-block, which drops to its own rows (8/9) so
// the main grid stays 18 wide.
const SEGMENTS: readonly { period: number; from: number; columns: readonly number[] }[] = [
  { period: 1, from: 1, columns: [1, 18] },
  { period: 2, from: 3, columns: [1, 2, 13, 14, 15, 16, 17, 18] },
  { period: 3, from: 11, columns: [1, 2, 13, 14, 15, 16, 17, 18] },
  { period: 4, from: 19, columns: range(1, 18) },
  { period: 5, from: 37, columns: range(1, 18) },
  { period: 6, from: 55, columns: [1, 2] },
  { period: 6, from: 72, columns: range(4, 18) },
  { period: 7, from: 87, columns: [1, 2] },
  { period: 7, from: 104, columns: range(4, 18) },
  { period: 8, from: 57, columns: range(3, 17) },
  { period: 9, from: 89, columns: range(3, 17) }
];

export const PERIODIC_TABLE: readonly PeriodicTableCell[] = SEGMENTS.flatMap(({ period, from, columns }) =>
  columns.map((column, index) => {
    const z = from + index;
    return { z, symbol: elementSymbol(z), name: ELEMENT_NAMES[z - 1]!, period, column };
  })
).sort((first, second) => first.z - second.z);
